<script>
import { mapGetters, mapActions } from 'vuex';
import InfoIcon from '@/V2/components/InfoIcon';
import DocumentsTitleUpload from '@/V2/documents/DocumentsTitleUpload';
import StatusBadge from '@/V2/components/status/StatusBadge.vue';
import CustomerFraudDocuments from '@/V2/customers/fraud/CustomerFraudDocuments.vue';
import CustomerFraudSummary from '@/V2/customers/fraud/CustomerFraudSummary.vue';
import CustomerFraudSignals from '@/V2/customers/fraud/CustomerFraudSignals.vue';
import CustomerFraudDistribution from '@/V2/customers/fraud/CustomerFraudDistribution.vue';
import CustomerFraudUploading from '@/V2/customers/fraud/CustomerFraudUploading.vue';
import { getTopRiskLevel } from '@/constants/riskLevels';
import { $event } from '@/bus';
import { hasAgentRunInProgress } from '@/utils/ai-agents';
import { registerChatAgentHookClickEvent } from '@/events-logger';

export default {
  components: {
    InfoIcon,
    DocumentsTitleUpload,
    StatusBadge,
    CustomerFraudDocuments,
    CustomerFraudSummary,
    CustomerFraudSignals,
    CustomerFraudDistribution,
    CustomerFraudUploading,
  },
  computed: {
    ...mapGetters([
      'currentConversationUuid',
      'isChatAgentEnabled',
      'getCachedCustomerReviewAgentRunsForCustomerUUID',
      'getCachedDocumentReviewAgentRunsForCustomerUUID',
      'someDocumentUploadingOrProcessing',
      'getCachedFraudSummary',
    ]),
    showDocumentsUploadingComponent() {
      return this.someDocumentUploadingOrProcessing;
    },
    fraudSummary() {
      return this.getCachedFraudSummary(this.$route.params.customer_id);
    },
    fraudSummaryIsLoading() {
      return typeof this.fraudSummary === 'undefined';
    },
    latestCustomerReviewAgentRun() {
      const runs = this.getCachedCustomerReviewAgentRunsForCustomerUUID(
        this.$route.params.customer_id,
      );
      return runs?.length > 0 ? runs[runs.length - 1] : null;
    },
    riskLevel() {
      return getTopRiskLevel(this.fraudSummary);
    },
    customerHasDocuments() {
      return (
        this.fraudSummary?.aggregated_risk_counts &&
        Object.values(this.fraudSummary?.aggregated_risk_counts).some(
          (value) => value > 0,
        )
      );
    },
    anyDocumentReviewAgentIsRunning() {
      const runs = this.getCachedDocumentReviewAgentRunsForCustomerUUID(
        this.$route.params.customer_id,
      );
      return hasAgentRunInProgress(runs);
    },
  },
  methods: {
    ...mapActions(['submitConversationMessage', 'openChat']),
    onUploadDocumentsClick() {
      const documentsTitleUpload = this.$refs.documentsTitleUpload;
      documentsTitleUpload.startUpload();
    },
    onOpenAssistantClicked() {
      this.openChat();
      registerChatAgentHookClickEvent('customer-open-assistant');
    },
  },
};
</script>

<template>
  <div
    class="customer-fraud i-px-11 i-pt-11 i-pb-11 i-flex"
    data-testid="customer-fraud-page"
  >
    <div class="customer-fraud-container d-flex flex-column">
      <!-- title with upload -->
      <documents-title-upload
        ref="documentsTitleUpload"
        title="Fraud"
        subtitle="View the fraud analysis results for this customer"
        :expanded="false"
      >
      </documents-title-upload>

      <!-- content -->
      <div class="fraud-content d-flex flex-column i-gap-8">
        <customer-fraud-uploading v-if="showDocumentsUploadingComponent" />

        <div class="d-flex flex-row i-gap-8">
          <!-- summary -->
          <customer-fraud-summary
            class="half-width"
            :summary="fraudSummary"
            :latest-customer-run="latestCustomerReviewAgentRun"
            :customer-has-documents="customerHasDocuments"
            :loading="fraudSummaryIsLoading"
            :document-agent-is-running="
              anyDocumentReviewAgentIsRunning || showDocumentsUploadingComponent
            "
            :button-text="isChatAgentEnabled ? 'Open Assistant' : null"
            @button-click="onOpenAssistantClicked"
          />

          <customer-fraud-distribution
            class="half-width"
            :loading="fraudSummaryIsLoading"
            :summary="fraudSummary"
          />
        </div>

        <!-- signals -->
        <customer-fraud-signals
          :latest-customer-run="latestCustomerReviewAgentRun"
          :show-loading-state="fraudSummaryIsLoading"
        />

        <!-- documents -->
        <customer-fraud-documents
          :risk-level="riskLevel"
          :fraud-summary="fraudSummary"
          @upload-documents="onUploadDocumentsClick"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.customer-fraud {
  overflow-y: auto;

  .customer-fraud-container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .half-width {
    width: 50%;

    :deep(.expandable-container),
    :deep(.content) {
      height: 100% !important;
    }
  }
}
</style>
